/// <amd-module name="Artifacts/Feed/components/CreateFeedPanel" />

import { FeedModule } from "Artifacts/Feed/redux";
import { CreateFeedActions } from "Artifacts/Feed/redux/Actions";
import { IFeedModuleState } from "Artifacts/Feed/redux/Module";
import * as Res from "Artifacts/Feed/Resources";
import { ICreateFeedOptions, Scope, Visibility } from "Artifacts/Feed/services/CreateFeedService";
import { CenteredSpinner } from "Artifacts/Root/components/CenteredSpinner";
import { ErrorBannerLoader } from "Artifacts/Root/components/ErrorBannerLoader";
import { ComponentState } from "Artifacts/Root/Constants";
import { registerModal } from "Artifacts/Root/containers/ModalManager";
import { getCurrentCollectionName, getIsHosted } from "Artifacts/Root/helpers/CollectionHelpers";
import { isLoading } from "Artifacts/Root/helpers/ComponentStateHelpers";
import {
  getCurrentSelectedProjectName,
  isCurrentSelectedProjectPublic
} from "Artifacts/Root/helpers/ProjectHelpers";
import { connectDynamicModules } from "Artifacts/Root/redux";
import * as RootRes from "Artifacts/Root/Resources";
import { feedHubRoute } from "Artifacts/Root/routing/Constants";
import { CreateFeedAdditionalData } from "Artifacts/Root/types/AzureArtifacts";
import { isValidFeedName } from "Artifacts/Root/Validator";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { generatePath, useHistory } from "react-router-dom";
import * as StringUtils from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { useComponentContext, usePageContext } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { Icon } from "VSSUI/Icon";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { TextFieldWithMessage } from "VSSUI/TextField";
import { getSafeId } from "VSSUI/Util";

export const CreateFeedModalId = "CreateFeedModal";

export interface ICreateFeedPanelProps {
  onDismiss: () => void;
}

interface ICreateFeedPanelMapStateToProps {
  componentState: ComponentState;
  serviceParams?: CreateFeedAdditionalData;
  createButtonComponentState: ComponentState;
}

const Actions = {
  createFeedAction: CreateFeedActions.createFeed,
  loadCreateFeedParams: CreateFeedActions.loadCreateFeedParams,
};

interface ICreateFeedPanelState {
  feedName: string;
  visibility: string;
  includePublicUpstreams: boolean;
  scope: string;
}

function getInitialFormState(aadUpstreamsEnabled: boolean): ICreateFeedPanelState {
  // Default visibility is based on whether or not the org is aad backed.
  // If public project, visibility should be public
  return {
    feedName: "",
    scope: Scope.Project,
    visibility: aadUpstreamsEnabled ? Visibility.AadTenant : Visibility.Collection,
    includePublicUpstreams: true,
  };
}

const CreateFeedPanel: React.FC<ICreateFeedPanelProps &
  ICreateFeedPanelMapStateToProps &
  typeof Actions> = ({
  onDismiss,
  componentState,
  serviceParams,
  createButtonComponentState,
  createFeedAction,
  loadCreateFeedParams,
}) => {
  useEffect(() => {
    loadCreateFeedParams();
  }, [loadCreateFeedParams]);

  const [panelState, setFormState] = useState<ICreateFeedPanelState>(() =>
    getInitialFormState(false),
  );

  // Set initial form state properly once we've loaded the params from the backend
  useEffect(() => {
    serviceParams && setFormState(getInitialFormState(serviceParams.isOrgAadBacked));
  }, [serviceParams]);

  const history = useHistory();

  const createFeed = useCallback(() => {
    if (isValidFeedName(panelState.feedName)) {
      const handleCreatedFeed = (feedIdentifier: string) => {
        onDismiss();
        history.push(generatePath(feedHubRoute, { feedIdentifier }));
        // Hard reload to trigger feedPicker to retrieve new list of feeds
        location.reload();
      };

      const feedData: ICreateFeedOptions = { name: panelState.feedName, ...panelState };
      serviceParams &&
        !isLoading(createButtonComponentState) &&
        createFeedAction(feedData, serviceParams, handleCreatedFeed);
    }
  }, [createButtonComponentState, createFeedAction, history, onDismiss, panelState, serviceParams]);

  const { pageContext } = useComponentContext();
  const { projectName, collectionName, isPublicProject } = useMemo(() => {
    return {
      projectName: getCurrentSelectedProjectName(pageContext),
      collectionName: getCurrentCollectionName(pageContext),
      isPublicProject: isCurrentSelectedProjectPublic(pageContext),
    };
  }, [pageContext]);
  const aadUpstreamsEnabled = (serviceParams?.isOrgAadBacked === undefined) ? true : serviceParams.isOrgAadBacked;
  const collectionScope = getIsHosted(pageContext) ? Res.Organization : Res.ProjectCollection;

  return (
    <Panel
      className="create-feed"
      titleProps={{
        text: Res.CreateFeed_PageTitle,
        id: "create-feed-panel-title",
      }}
      description={Res.CreateFeed_Description}
      onDismiss={/** Handle resetting the error state here so message bar is dismissed*/ onDismiss}
      overlayContent={
        isLoading(createButtonComponentState) ? (
          <CenteredSpinner label={RootRes.Submitting} />
        ) : isLoading(componentState) ? (
          <CenteredSpinner />
        ) : (
          undefined
        )
      }
      footerButtonProps={[
        { text: Res.Dialog_CancelButtonText, onClick: onDismiss },
        {
          text: Res.CreateFeed_Create,
          primary: true,
          disabled: !isValidFeedName(panelState.feedName),
          onClick: createFeed,
        },
      ]}
    >
      <ErrorBannerLoader state={createButtonComponentState} noLoadingSpinner>
        <div>
          <TextFieldWithMessage
            className="create-feed-section"
            message={
              !isValidFeedName(panelState.feedName) && panelState.feedName !== ""
                ? Res.CreateFeed_FeedNameError
                : ""
            }
            error={!isValidFeedName(panelState.feedName) && panelState.feedName !== ""}
            textFieldProps={{
              label: Res.CreateFeed_Name,
              required: true,
              value: panelState.feedName,
              maxLength: 64,
              onChange: (event, newFeedName) => {
                setFormState({
                  ...panelState,
                  feedName: newFeedName,
                });
              },
              ariaDescribedBy: "feedname-instructions",
            }}
          />
          <span className="visibility-hidden" id={getSafeId("feedname-instructions")}>
            {
              // This message is only for the ariaDescribedBy value of the feed name textfield.
              // Keeping for now because of a11y
              Res.CreateFeed_FeedNameError
            }
          </span>
          {isPublicProject ? (
            <PublicProjectContent />
          ) : (
            <PrivateProjectContent
              collectionName={collectionName}
              aadUpstreamsEnabled={aadUpstreamsEnabled}
              panelState={panelState}
              setFormState={setFormState}
            />
          )}
          {
            <ScopeContent
              projectName={projectName}
              panelState={panelState}
              setFormState={setFormState}
              collectionScope={collectionScope}
            />
          }
        </div>
      </ErrorBannerLoader>
    </Panel>
  );
};

export interface IScopeContentProps {
  projectName: string | null;
  panelState: ICreateFeedPanelState;
  setFormState: React.Dispatch<React.SetStateAction<ICreateFeedPanelState>>;
  collectionScope: string;
}

const ScopeContent: React.FC<IScopeContentProps> = ({ projectName, panelState, setFormState, collectionScope }) => {
  projectName = projectName || "";

  return (
    <fieldset>
      <legend className="title-xs">{Res.Scope}</legend>

      <RadioButtonGroup
        selectedButtonId={panelState.scope}
        onSelect={(buttonId) => setFormState({ ...panelState, scope: buttonId })}
      >
        <RadioButton id={Scope.Project}>
          <div className="bolt-radio-button-label">
            {StringUtils.format(Res.CreateFeed_ProjectHeading, projectName)}
            <span className="create-feed-section create-feed-helptext">
              {StringUtils.format(Res.CreateFeed_ProjectScopedDescription, projectName)}
            </span>
          </div>
        </RadioButton>
        <RadioButton id={Scope.Organization} text={collectionScope} />
      </RadioButtonGroup>
    </fieldset>
  );
};

const PublicProjectContent: React.FC = () => {
  return (
    <div>
      <legend className="title-xs">{Res.CreateFeed_Visibility}</legend>
      <div>
        <Icon ariaLabel={Res.AriaLabel_GlobeIcon} iconName="Globe" className="create-feed-globe" />
        <span>{Res.Public}</span>
      </div>
      <p className="create-feed-helptext">{Res.CreateFeed_PublicProjectDescription}</p>
    </div>
  );
};

export interface IPrivateProjectContentProps {
  collectionName: string;
  aadUpstreamsEnabled: boolean;
  panelState: ICreateFeedPanelState;
  setFormState: React.Dispatch<React.SetStateAction<ICreateFeedPanelState>>;
}

const PrivateProjectContent: React.FC<IPrivateProjectContentProps> = ({
  collectionName,
  aadUpstreamsEnabled,
  panelState,
  setFormState,
}) => {
  return (
    <React.Fragment>
      <fieldset key="visibility">
        <legend className="title-xs">{Res.CreateFeed_Visibility}</legend>
        <RadioButtonGroup
          className="fontSizeM create-feed-section"
          selectedButtonId={panelState.visibility}
          onSelect={(selectedButtonId: string) =>
            setFormState({ ...panelState, visibility: selectedButtonId })
          }
        >
          {getPrivateVisibilityRadioOptions(collectionName, aadUpstreamsEnabled, usePageContext())}
        </RadioButtonGroup>
      </fieldset>
      <fieldset key="upstream">
        <legend className="title-xs">{Res.CreateFeed_UpstreamSources}</legend>
        <Checkbox
          label={Res.CreateFeed_IncludeUpstreams}
          checked={panelState.includePublicUpstreams}
          onChange={(_event, checked) =>
            setFormState({ ...panelState, includePublicUpstreams: checked })
          }
          ariaDescribedBy="create-feed-upstream-helptext"
        />
        <p id={getSafeId("create-feed-upstream-helptext")} className="create-feed-helptext">
          {Res.CreateFeed_UpstreamExample}
        </p>
      </fieldset>
    </React.Fragment>
  );
};

function getPrivateVisibilityRadioOptions(
  collectionName: string,
  aadUpstreamsEnabled: boolean,
  pageContext: IVssPageContext,
): JSX.Element[] {
  const options: JSX.Element[] = [];

  if (aadUpstreamsEnabled) {
    const aadVisibilityOptionElement = getVisibilityOptionElement(
      Visibility.AadTenant,
      "aad-tenant-visibility-radio-button-label",
      StringUtils.format(Res.CreateFeed_MembersOf_Header, Res.CreateFeed_AadTenantVisibility),
      Res.CreateFeed_AadTenantVisibility_Description,
    );

    options.push(aadVisibilityOptionElement);
  }

  const orgFeedDescription = getIsHosted(pageContext) ? Res.CreateFeed_MemberVisibility_Description_Organization : 
    Res.CreateFeed_MemberVisibility_Description_OnPremProject;

  const collectionVisibilityOptionElement = getVisibilityOptionElement(
    Visibility.Collection,
    "collection-visibility-radio-button-label",
    StringUtils.format(Res.CreateFeed_MembersOf_Header, collectionName),
    orgFeedDescription,
  );

  const privateVisibilityOptionElement = getVisibilityOptionElement(
    Visibility.Private,
    "private-visibility-radio-button-label",
    Res.CreateFeed_PrivateVisibility_Header,
    Res.CreateFeed_PrivateVisibility_Description,
  );

  options.push(collectionVisibilityOptionElement);
  options.push(privateVisibilityOptionElement);

  return options;
}

function getVisibilityOptionElement(
  visibility: string,
  elementId: string,
  radioButtonHeader: string,
  description: string,
): JSX.Element {
  return (
    <RadioButton key={visibility} id={visibility} ariaLabelledBy={elementId}>
      <div className="bolt-radio-button-label" id={getSafeId(elementId)}>
        {radioButtonHeader}
        <span className="create-feed-helptext" id={getSafeId(`${elementId}-helptext`)}>
          {description}
        </span>
      </div>
    </RadioButton>
  );
}

const mapStateToProps = (state: IFeedModuleState): ICreateFeedPanelMapStateToProps => {
  const panelState = state["feed"].createFeedPanel;
  return {
    componentState: panelState.componentState,
    serviceParams: panelState.serviceParams,
    createButtonComponentState: panelState.createButtonComponentState,
  };
};

const ConnectedCreateFeedPanel = connectDynamicModules<ICreateFeedPanelProps>(
  connect<ICreateFeedPanelMapStateToProps, unknown, ICreateFeedPanelProps>(
    mapStateToProps,
    Actions,
  )(CreateFeedPanel),
  FeedModule,
);

registerModal(CreateFeedModalId, ConnectedCreateFeedPanel);

export { ConnectedCreateFeedPanel as CreateFeedPanel };

